import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom, Subject, take, takeUntil } from 'rxjs';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';

@Component({
  selector: 'app-credit-transactions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DatePipe,
    DecimalPipe,
    BtnBackComponent,
  ],
  templateUrl: './credit-transactions.component.html',
  styleUrl: './credit-transactions.component.css',
})
export class CreditTransactionsComponent implements OnInit, OnDestroy {
  groupId: string | null = null;
  startDate: string = '';
  tillDate: string = '';
  loading: boolean = false;
  aiTransactions: any[] = [];

  private exit$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private afs: AngularFirestore,
    private toastr: ToastrService
  ) {
    const today = new Date();
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    this.startDate = this.formatDateForInput(sevenDaysAgo);
    this.tillDate = this.formatDateForInput(today);
  }

  ngOnInit(): void {
    // Check direct paramMap first, then parent route paramMap
    this.route.paramMap.pipe(takeUntil(this.exit$)).subscribe((params) => {
      let id = params.get('id');
      if (!id && this.route.parent) {
        id = this.route.parent.snapshot.paramMap.get('id');
      }
      this.groupId = id;
      if (this.groupId) {
        this.searchTransactions();
      }
    });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  private formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  async searchTransactions(): Promise<void> {
    if (!this.groupId) return;
    this.loading = true;

    const d1 = new Date(`${this.startDate}T00:00:00`);
    const d2 = new Date(`${this.tillDate}T00:00:00`);

    const minDate = d1 < d2 ? d1 : d2;
    const maxDate = d1 > d2 ? d1 : d2;

    const lowerTs = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate(), 0, 0, 0, 0);
    const upperTs = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate(), 23, 59, 59, 999);

    console.log('[CreditTransactions] searchTransactions initiated:', {
      groupId: this.groupId,
      startDate: this.startDate,
      tillDate: this.tillDate,
      lowerTs: lowerTs.toISOString(),
      upperTs: upperTs.toISOString(),
    });

    try {
      let snap: any[];
      try {
        snap = await lastValueFrom(
          this.afs
            .collection(`group_tokens/${this.groupId}/credit_ledger`, (ref) =>
              ref.orderBy('timestamp', 'desc')
            )
            .snapshotChanges()
            .pipe(take(1))
        );
      } catch (orderErr) {
        console.warn('[CreditTransactions] orderBy timestamp failed, querying unordered:', orderErr);
        snap = await lastValueFrom(
          this.afs
            .collection(`group_tokens/${this.groupId}/credit_ledger`)
            .snapshotChanges()
            .pipe(take(1))
        );
      }

      const rawItems = (snap || []).map((docSnap: any) => ({
        id: docSnap.payload.doc.id,
        ...(docSnap.payload.doc.data() as any),
      }));

      console.log(
        `[CreditTransactions] Total raw credit_ledger items fetched from Firestore: ${rawItems.length}`,
        rawItems
      );

      // Robust date filtering across Timestamps, ISO strings, Epoch, or createdAt fields
      const filteredItems = rawItems.filter((item: any) => {
        const rawTs = item.timestamp || item.createdAt || item.date;
        if (!rawTs) return true; // Do not drop items with missing timestamp

        let itemDate: Date;
        if (rawTs?.toDate && typeof rawTs.toDate === 'function') {
          itemDate = rawTs.toDate();
        } else if (rawTs?.seconds) {
          itemDate = new Date(rawTs.seconds * 1000);
        } else {
          itemDate = new Date(rawTs);
        }

        if (isNaN(itemDate.getTime())) return true; // Do not drop unparseable dates

        return itemDate >= lowerTs && itemDate <= upperTs;
      });

      // Sort descending by date
      filteredItems.sort((a: any, b: any) => {
        const getMs = (item: any) => {
          const rawTs = item.timestamp || item.createdAt || item.date;
          if (rawTs?.toDate) return rawTs.toDate().getTime();
          if (rawTs?.seconds) return rawTs.seconds * 1000;
          if (rawTs) return new Date(rawTs).getTime();
          return 0;
        };
        return getMs(b) - getMs(a);
      });

      console.log(
        `[CreditTransactions] Filtered items for range [${lowerTs.toISOString()} to ${upperTs.toISOString()}]: found ${filteredItems.length} records`,
        filteredItems
      );

      this.aiTransactions = filteredItems;
    } catch (err) {
      console.error('[CreditTransactions] Error fetching AI transactions:', err);
      this.toastr.error('Failed to load AI usage transactions');
    } finally {
      this.loading = false;
    }
  }

  formatFeatureTitle(tx: any): string {
    const rawFeature = tx?.feature || tx?.metadata?.feature || '';
    if (!rawFeature) {
      return 'AI Feature';
    }
    const featureMap: Record<string, string> = {
      notice_generation: 'Notice Generation',
      notice_image: 'Notice Image',
      notice_image_generation: 'Notice Image Generation',
      // Legacy key kept for older ledger entries recorded before the vendor name was removed.
      notice_image_generation_nano_banana: 'Notice Image Generation',
      quiz_generation: 'Quiz Question Generation',
      quiz_grading: 'Quiz Grading',
      receipt_scan: 'Receipt Scanning',
      accounting_receipt_extraction: 'Receipt Extraction',
      ai_generation: 'AI Generation',
    };

    if (featureMap[rawFeature]) {
      return `AI Feature: ${featureMap[rawFeature]}`;
    }

    return `AI Feature: ${this.stripVendorNames(rawFeature)}`;
  }

  /**
   * Safety net for any feature key we haven't explicitly mapped above: formats it into a readable
   * title while stripping known third-party AI vendor/model names so they never surface in a
   * member-facing transaction description.
   */
  private stripVendorNames(rawFeature: string): string {
    const vendorTerms = [
      'nano banana',
      'nanobanana',
      'gemini',
      'imagen',
      'vertex ai',
      'vertexai',
      'chatgpt',
      'openai',
      'gpt-4',
      'gpt4',
      'gpt',
      'claude',
      'anthropic',
      'dall-e',
      'dalle',
      'stability ai',
      'midjourney',
    ];

    let cleaned = rawFeature.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
    for (const term of vendorTerms) {
      cleaned = cleaned.replace(new RegExp(term, 'gi'), '');
    }

    return cleaned
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (c: string) => c.toUpperCase()) || 'AI Generation';
  }
}
